import React, { useState, useEffect, forwardRef, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import moment from 'moment';
import ButtonGreen from '../Buttons/ButtonGreen';
import { openChatElement, openSingleChat, } from '../../actions/conversations';
import { useNavigate, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Popover from '@mui/material/Popover';
import { Avatar, Skeleton } from '@mui/material';
import { enableRoom, getUserCallStatusAction, getUserDetailsByID } from '../../actions/users';

import NoResultFound from '../NoResultFound';
import IconToolTip from '../IconToolTip';
import { openCustomModal } from '../../actions/modal';
import UpdateAvailabilityStatus from '../../pages/team/Modals/UpdateAvailabilityStatus';
import { getMaskedString, isLogged } from '../../utils/common';
import ButtonScogoPrimary from '../Buttons/ButtonScogoPrimary';
import { getSingleConvoIdFromUserIds } from '../../utils/utils';
import { websocketEvents } from '../../utils/websocket';
import { sendDataThroughWebsocket } from '../../actions/websocket';
import { generateRoomLink } from '../../pages/videomeet/components/constants';
import { chatPageUsingLink, chats } from '../../router/roleWisePageRoute';
import { canUpdateUserCalender, canViewUserCalender } from '../../utils/access';

const ShowSkeleton = () => {
    return (
        <>
            <div className='flex items-center gap-x-4'>
                <Skeleton variant='circular'>
                    <Avatar sx={{ height: '8rem', width: '9rem' }} />
                </Skeleton>
                <div>
                    <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={140} height={20} animation='wave' />
                    <br />
                    <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={140} height={20} animation='wave' />
                </div>
            </div>
            <div className='flex gap-x-2 mt-2'>
                <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={120} height={20} animation='wave' />

                <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={120} height={20} animation='wave' />
            </div>
            <div className='flex items-center gap-x-2 mt-3'>
                {[1, 2, 3].map(() => {
                    return (
                        <div>
                            <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={79} height={20} animation='wave' />
                            <br />
                            <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={79} height={20} animation='wave' />
                        </div>
                    );
                })}
            </div>
            <br />
            <Skeleton sx={{ background: 'rgb(221,221,221)' }} variant='rectangular' width={250} height={20} animation='wave' />
        </>
    );
};

const Popup = forwardRef(({ userId, from, close }, ref) => {
    const dispatch = useDispatch();

    const userDetailsById = useSelector((state) => state.user.userDetailsById);
    const isUserDetailsByIdLoading = useSelector((state) => state.loading.isUserDetailsByIdLoading);
    const { loggedUser } = useSelector((state) => state.auth);
    const { isEnableVCUserLoading } = useSelector((state) => state.loading);
    const { formButtonLoading } = useSelector(state => state.loading);
    const navigate = useNavigate();
    const matchChatPage = useMatch(chats.path);
    const matchChatPageUsingLink = useMatch(chatPageUsingLink.path);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const isChatPage = matchChatPage || matchChatPageUsingLink;

    const memoizedUserDetails = useMemo(() => {
        let data = { user: {}, org: {} };
        if (userDetailsById[userId]) {
            data = userDetailsById[userId];
        }
        return data;
    }, [userDetailsById, userId]);

    const customTags = useMemo(() => {
        const tags = memoizedUserDetails?.user?.tags;
        if (!Array.isArray(tags)) return [];
        return tags.filter((tag) => tag?.tag_type === 'CUSTOM');
    }, [memoizedUserDetails]);

    const showAttendanceIcon = canUpdateUserCalender(newLoggedUserData.actions) || canViewUserCalender(newLoggedUserData.actions);

    useEffect(() => {
        if (userId && !userDetailsById[userId]) {
            dispatch(getUserDetailsByID({ user_id: userId }));
        }
    }, [userId, dispatch]);

    const showContacts = memoizedUserDetails?.user?.mobile || memoizedUserDetails?.user?.email;
    const showTicket = memoizedUserDetails?.user?.numTickets || memoizedUserDetails?.user?.numTickets;

    if (!memoizedUserDetails && !isUserDetailsByIdLoading) {
        return <div className="py-4 px-4" >
            <NoResultFound message="User Details Not Found" paddingy="py-2" paddingx="px-2" />

        </div>
    }

    const addressProofOnClick = () => {
        if (memoizedUserDetails.gstNumber) {
            return false
        } else if (memoizedUserDetails.panNumber) {
            return false
        } else if (memoizedUserDetails?.addressProofUrl) {
            return true
        } else {
            return false
        }
    }

    const singleConversationId = getSingleConvoIdFromUserIds(loggedUser?.id, userId);

    const handleOnSuccess = async ({ room_id, conversationId }) => {
        dispatch(getUserCallStatusAction({ userId: loggedUser?.id, status: [1, 2, 3, 4] }));
        dispatch(sendDataThroughWebsocket({ action: websocketEvents.chat.videoMeetInvite, data: { userIds: [userId], videoMeetDetails: { videoMeetLink: generateRoomLink(room_id, conversationId), room_id: room_id } } }));
        window.open(generateRoomLink(room_id, conversationId), "_blank");
        close();
    }

    const handleEnableRoom = () => {
        dispatch(enableRoom({ userId: loggedUser?.id, conversationId: singleConversationId, onSuccess: handleOnSuccess, buttonName: `popup-scogo-connect-${userId}` }));
    }

    let proofNumber = (memoizedUserDetails?.addressProofNumber || memoizedUserDetails?.gstNumber || memoizedUserDetails?.panNumber);
    if (memoizedUserDetails?.addressProofNumber) {
        proofNumber = loggedUser?.frontend_controller?.ViewMaskData ? memoizedUserDetails?.addressProofNumber : getMaskedString(memoizedUserDetails?.addressProofNumber);
    } else if (memoizedUserDetails?.gstNumber) {
        proofNumber = memoizedUserDetails.gstNumber
    } else if (memoizedUserDetails?.panNumber) {
        proofNumber = loggedUser?.frontend_controller?.ViewMaskData ? memoizedUserDetails?.panNumber : getMaskedString(memoizedUserDetails?.panNumber);
    }

    const openChat = (userId, buttonName) => {
        if (isChatPage) {
            dispatch(openSingleChat({ userId, openInNewTab: true, navigate, formName: buttonName }));
        } else {
            dispatch(openChatElement({ source: 'teams', buttonName, userId }));

        }
    }
    let isLoading = formButtonLoading?.[`open_chat_${userId}`]
    return (
        <div className='bg-white shadow-lg rounded-md p-5 max-w-[32rem] relative' ref={ref}>
            {showAttendanceIcon && !isUserDetailsByIdLoading && (
                <IconToolTip title='View Attendance'>
                    <span
                        className='material-icons text-font18 text-scogoprimary hover:text-scogoorange cursor-pointer transition-colors absolute top-3 right-3'
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                            dispatch(openCustomModal({
                                heading: `View Attendance for ${memoizedUserDetails?.user?.full_name || memoizedUserDetails?.userName}`,
                                modalWidth: '60rem',
                                modalHeight: 'auto',
                                modalComponent: <UpdateAvailabilityStatus user_id={userId} full_name={memoizedUserDetails?.user?.full_name || memoizedUserDetails?.userName} actions={[]} />
                            }));
                        }}
                    >
                        event_available
                    </span>
                </IconToolTip>
            )}
            {isUserDetailsByIdLoading ? (
                <ShowSkeleton />
            ) : (
                <>
                    <div className='flex gap-4'>
                        <div>

                            <img src={memoizedUserDetails?.user?.avatar_link} className='w-[7rem] h-[7rem] shadow-md rounded-full mb-1 outline-2 object-cover shrink-0' alt='Profile' />

                            {/* {memoizedUserDetails?.rating > 0 ? <RatingComponent rating={memoizedUserDetails?.rating} /> : <></>} */}

                        </div>
                        <div className='flex flex-col justify-center overflow-hidden'>
                            <span className='text-font16 whitespace-nowrap text-ellipsis overflow-hidden' title={memoizedUserDetails?.userName}>
                                {memoizedUserDetails?.userName}
                            </span>
                            {(memoizedUserDetails?.user?.full_name) && <div>
                                <span className='text-font11 text-scogogray'>{memoizedUserDetails?.user?.full_name}</span>
                            </div>}
                            <div className='flex gap-5'>
                                <div className='flex gap-2'>
                                    {memoizedUserDetails?.user?.roles?.map((role, index) => {
                                        const isRE = String(role?.role?.label).toUpperCase() === 'RE';
                                        const roleColorClasses = isRE
                                            ? 'bg-green-50 text-[#089000] border-[#089000]'
                                            : 'bg-orange-50 text-scogoorange border-scogoorange';
                                        return (
                                            <span key={index} className={`max-h-max px-2 py-0.5 text-sm font-medium rounded-full border ${roleColorClasses}`}>
                                                {role.role.label}
                                            </span>
                                        );
                                    })}
                                </div>
                                {(proofNumber) && (
                                    <div
                                        className='flex gap-1 items-center border-scogoprimary border-1 cursor-pointer px-2 py-0 rounded'
                                        onClick={() => {
                                            return addressProofOnClick() ? window.open(memoizedUserDetails?.addressProofUrl, '_blank') : ""
                                        }}
                                    >
                                        <span className='material-icons text-font09 text-scogoorange m-0'>contact_mail</span>
                                        <span className='text-scogoprimary text-font09 m-0'>{proofNumber}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {showContacts && (
                        <div className='grid grid-cols-2 w-full gap-4 p-2 mt-4'>
                            {memoizedUserDetails?.user?.email && (
                                <div className='flex text-font12 gap-2 items-center'>
                                    <span className='material-icons text-font18'>email</span>
                                    <span className='whitespace-nowrap text-ellipsis overflow-hidden' title={memoizedUserDetails?.user?.email}>
                                        {memoizedUserDetails?.user?.email}
                                    </span>
                                </div>
                            )}
                            {memoizedUserDetails?.user?.mobile && (
                                <div className='flex text-font12 gap-2 items-center'>
                                    <span className='material-icons text-font18'>call</span>
                                    <span>{memoizedUserDetails?.user?.mobile}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {customTags.length > 0 && (
                        <div className='px-2 mt-3'>
                            <span className='text-font13'>Tags</span>
                            <div className='flex flex-wrap gap-2 mt-2'>
                                {customTags.map((tag, index) => (
                                    <span
                                        key={tag.id || tag.tag_id || index}
                                        title={tag.text}
                                        className='bg-tagbg text-scogoprimary text-font10 flex items-center gap-1 rounded-3px px-2 py-1 max-w-full'
                                    >
                                        {tag.icon_url && (
                                            <img src={tag.icon_url} alt='' className='w-[1.6rem] h-[1.6rem] object-contain shrink-0' />
                                        )}
                                        <span className='whitespace-nowrap text-ellipsis overflow-hidden'>{tag.text}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className='grid grid-cols-3 w-full gap-4 p-2'>
                        <div className='flex flex-col gap-2'>
                            <span className='text-font13'>Joined on</span>
                            <span className='bg-scogoLogoGreen  text-white max-w-max text-font10 px-2 py-1 rounded-3px'>
                                {moment(memoizedUserDetails?.user?.created_at).format('DD MMM YYYY')}
                            </span>
                        </div>
                        {showTicket && (
                            <div className='flex flex-col gap-2'>
                                <span className='text-font13'>No. Tickets</span>
                                <span className='text-font12'>
                                    {memoizedUserDetails?.numTicketsClosed} / {memoizedUserDetails?.numTickets}
                                </span>
                            </div>
                        )}
                        {memoizedUserDetails?.balance > 0 && (
                            <div className='flex flex-col gap-2'>
                                <span className='text-font13'>Balance</span>
                                <div className='flex gap-1 items-center'>
                                    <span className='material-icons text-font13'>currency_rupee</span>
                                    <span className='text-font12'>{memoizedUserDetails.balance}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    {memoizedUserDetails?.chatId && (
                        <div className='w-full flex gap-2 px-2'>
                            <ButtonGreen
                                textOrComponent={
                                    <div className='flex items-center gap-2'>
                                        <img src='/img/chat_colourful.png' alt='' className='w-6 cursor-pointer' />
                                        Chat
                                    </div>
                                }
                                buttonClass='w-1/2'
                                buttonPadding='px-4 py-2 mt-4 w-1/2'
                                onClick={() => {
                                    // dispatch(openSingleChat({ userId, chatId: memoizedUserDetails.chatId, navigate, openInNewTab: true }));
                                    // close();

                                    openChat(userId, `open_chat_${userId}`)

                                }}
                                loading={isLoading}
                            />
                            <ButtonScogoPrimary
                                textOrComponent={
                                    <div className='flex items-center gap-2'>
                                        <div className="flex items-center w-7 h-7">
                                            <img src={process.env.PUBLIC_URL + '/img/scogo-connect-blue.svg'} alt="" className="w-full h-full object-contain hover:cursor-pointer" />
                                        </div>
                                        Connect
                                    </div>
                                }
                                loading={isEnableVCUserLoading}
                                buttonClass='w-1/2'
                                buttonPadding='px-4 py-2 mt-4 w-1/2'
                                onClick={() => {
                                    handleEnableRoom();
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

const UserPopup = ({
    children,
    userId,
    from,
    anchorOrigin = {
        vertical: 'top',
        horizontal: 'center',
    },
    transformOrigin = {
        vertical: 'bottom',
        horizontal: 'center',
    },
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    if (!isLogged() || !newLoggedUserData) {
        return <></>
    }
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    if (!userId) return <>{children}</>

    return (
        <>
            <div aria-describedby={id} onClick={handleClick} variant='contained' className='w-full hover:underline cursor-pointer inline'>
                {children}
            </div>
            <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={anchorOrigin} transformOrigin={transformOrigin}>
                <Popup userId={userId} from={from} showChat={userId !== newLoggedUserData.user.id} close={handleClose} />
            </Popover>
        </>
    );
};

export default UserPopup;
